import { useEffect, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { g, p, del, pa, eur, errMsg, ITEM_STATES } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Minus, Trash2, Search, Wine, Send, Printer, ArrowLeft, Leaf, Ban, Scale, ChevronRight, Zap, Pause, CornerDownRight, MessageSquare, ClipboardList } from "lucide-react";
import { FoglioComanda } from "@/components/FoglioComanda";
import { AggiungiPiatto, AnnullaPiatto } from "@/components/AggiungiPiatto";
import { cercaPiatti } from "@/lib/ricerca";
import Conto from "@/pages/Conto";
import { SpiegaGiri } from "@/components/SpiegaGiri";

const DEPT_BADGE = {
  Cucina: "bg-orange-100 text-orange-900",
  Pizzeria: "bg-red-100 text-red-900",
  Bar: "bg-sky-100 text-sky-900",
  Pasticceria: "bg-fuchsia-100 text-fuchsia-900",
};

// I reparti di produzione lavorano sui piatti, non sui soldi: a loro gli importi non servono.
const RUOLI_SENZA_IMPORTI = ["cuoco", "pizzaiolo", "pasticciere", "barista", "lavapiatti", "manutentore"];

export default function Comanda() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, departments, settings } = useApp();
  const { t, lang } = useI18n();
  const [order, setOrder] = useState(null);
  const [menu, setMenu] = useState([]);
  const [cat, setCat] = useState(null);
  const [rep, setRep] = useState(null);        // reparto scelto: Pizzeria, Cucina, Bar
  const [consigliati, setConsigliati] = useState([]);
  const [ingredienti, setIngredienti] = useState([]);
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [wines, setWines] = useState(null);
  const [checked, setChecked] = useState({});
  const [contoOpen, setContoOpen] = useState(false);
  const [ordinatoOpen, setOrdinatoOpen] = useState(false);
  const [giroAperto, setGiroAperto] = useState("");
  const [giri, setGiri] = useState({ sent: [], waiting: [] });
  const vedeImporti = !RUOLI_SENZA_IMPORTI.includes(user?.role);
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [modifiche, setModifiche] = useState([]);
  const [summary, setSummary] = useState(null);
  const [optionsFor, setOptionsFor] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [fisso, setFisso] = useState(null);        // menu del giorno: primi, secondi, contorni
  const [comporre, setComporre] = useState(null);  // voce del menu fisso da comporre
  const [fissoQty, setFissoQty] = useState(1);
  const [fissoGiri, setFissoGiri] = useState({ primi: 1, secondi: 2, contorni: 2 });
  const [scelte, setScelte] = useState({ primi: "", secondi: "", contorni: "" });
  const [reqFor, setReqFor] = useState(null);      // riga già mandata da correggere al volo
  const [foglioOpen, setFoglioOpen] = useState(false);
  const [foglioPref, setFoglioPref] = useState(() => localStorage.getItem("rh_vista_comanda") === "foglio");
  const [afterFor, setAfterFor] = useState(null);  // scelta del piatto di riferimento

  useEffect(() => {
    g("/options").then((o) => setReasons(o.cancel_reasons)).catch(() => setReasons([]));
    g("/menu-fisso").then(setFisso).catch(() => setFisso(null));
    g("/menu/consigliati").then(setConsigliati).catch(() => setConsigliati([]));
    g("/menu/ingredienti").then(setIngredienti).catch(() => setIngredienti([]));
  }, []);

  const load = useCallback(async () => {
    let o;
    try {
      o = await g(`/orders/${id}`);
    } catch (e) {
      // Comanda chiusa o non più esistente: si torna in sala invece di mostrare un errore tecnico.
      toast.error(e?.response?.status === 404 ? t("c.gone") : errMsg(e));
      return nav("/sala");
    }
    const m = await g("/menu");
    setOrder(o);
    setMenu(m);
    setGiri(await g(`/orders/${id}/giri`).catch(() => ({ sent: [], waiting: [] })));
    setWines(await g(`/orders/${id}/wines`).catch(() => null));
    setChecked((c) => {
      const next = { ...c };
      o.items.filter((i) => !i.sent).forEach((i) => (next[i.id] = next[i.id] ?? true));
      return next;
    });
  }, [id, nav, t]);

  useEffect(() => {
    load();
    // La comanda resta aperta a lungo: i tempi dei giri si aggiornano da soli.
    const i = setInterval(load, 20000);
    return () => clearInterval(i);
  }, [load]);

  // Col carrello aperto il pulsante del questionario si fa da parte (vedi index.css)
  useEffect(() => {
    document.body.dataset.cart = cart.length ? "1" : "";
    return () => {
      document.body.dataset.cart = "";
    };
  }, [cart.length]);

  // Prima il reparto, poi le sue categorie in ordine di giro: la sala trova il piatto dove se lo aspetta
  const reparti = useMemo(() => {
    const ordine = { Pizzeria: 1, Cucina: 2, Bar: 3 };
    return [...new Set(menu.map((m) => m.department))].sort(
      (a, b) => (ordine[a] || 9) - (ordine[b] || 9) || a.localeCompare(b));
  }, [menu]);

  const cats = useMemo(() => {
    const dentro = menu.filter((m) => !rep || m.department === rep);
    const giro = {};
    dentro.forEach((m) => {
      giro[m.category] = Math.min(giro[m.category] ?? 9, m.course || 9);
    });
    return Object.keys(giro)
      // Le voci del menu a prezzo fisso hanno il loro tasto in alto: niente doppioni qui sotto.
      .filter((c) => dentro.some((m) => m.category === c && !m.fixed_menu))
      .sort((a, b) => giro[a] - giro[b] || a.localeCompare(b));
  }, [menu, rep]);

  useEffect(() => {
    if (!rep && reparti.length) setRep(reparti[0]);
  }, [reparti, rep]);

  // I ritocchi più chiesti davvero: due tocchi invece di scriverli a mano.
  useEffect(() => {
    g("/takeaway/modifiche").then((r) => setModifiche(r.modifiche || [])).catch(() => {});
  }, []);


  const delGiorno = useMemo(() => menu.filter((m) => (m.tags || []).includes("giorno")), [menu]);
  // Il menu a prezzo fisso sta vicino ai reparti: è una scelta di sala, non una categoria di cucina.
  const vociFisse = useMemo(() => menu.filter((m) => m.fixed_menu), [menu]);

  useEffect(() => {
    if (cats.length && cat && !cat.startsWith("__") && !cats.includes(cat)) setCat(cats[0]);
    if (cats.length && !cat) setCat(cats[0]);
  }, [cats, cat]);

  if (!order) return <p className="text-sm text-slate-500">{t("c.loading")}</p>;

  const shown = q
    ? cercaPiatti(menu, q)
    : cat === "__giorno"
      ? delGiorno
      : cat === "__fisso"
        ? vociFisse
        : cat === "__top"
          ? consigliati.map((mid) => menu.find((m) => m.id === mid)).filter(Boolean)
          : menu.filter((m) => m.department === rep && m.category === cat);

  // Il giro consigliato tiene conto dei giri già usciti: il backend fa lo stesso
  const giroMandato = Math.max(0, ...order.items.filter((i) => i.sent).map((i) => i.giro || 1));
  const giroConsigliato = (m) => Math.max(m.course || 1, giroMandato + 1);

  const add = (m, surcharge = 0, label = "", opts = {}) =>
    setCart((c) => [
      ...c,
      {
        key: Math.random().toString(36).slice(2), menu_item_id: m.id, name: opts.nome || m.name,
        price: opts.incluso ? 0 : (m.by_weight && opts.grams ? Math.round((m.price_kg * opts.grams) / 10) / 100 : m.price),
        qty: opts.qty || 1, note: opts.note || "", surcharge, surcharge_label: label, course: m.course,
        giro: opts.giro || giroConsigliato(m),
        extras: opts.extras || [], removed: opts.removed || [], grams: opts.grams || 0, cooking: opts.cooking || "",
        takeaway: !!opts.takeaway, to_cut: !!opts.to_cut, incluso: !!opts.incluso, nome: opts.nome || "",
        solo_conto: !!opts.solo_conto,
      },
    ]);

  const extraTotal = (i) => (i.extras || []).reduce((s, e) => s + e.amount, 0);

  const cancelItem = async (reason) => {
    try {
      await p(`/orders/${id}/items/${cancelFor.id}/cancel`, { reason, by: user.name });
      setCancelFor(null);
      toast.success(t("x.cancel.ok"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const chQty = (key, d) =>
    setCart((c) => c.flatMap((x) => (x.key === key ? (x.qty + d > 0 ? [{ ...x, qty: x.qty + d }] : []) : [x])));

  const sendToOrder = async () => {
    if (!cart.length) return toast.error(t("c.needItem"));
    try {
      await p(`/orders/${id}/items`, cart.map(({ menu_item_id, qty, note, surcharge, surcharge_label, course, giro, extras, removed, grams, cooking, takeaway, to_cut, incluso, nome, solo_conto }) => ({ menu_item_id, qty, note, surcharge, surcharge_label, course, giro, extras, removed, grams, cooking, takeaway, to_cut, incluso, nome, solo_conto })));
      setCart([]);
      toast.success(t("c.added"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const pending = order.items.filter((i) => !i.sent);
  const sent = order.items.filter((i) => i.sent);
  const selIds = pending.filter((i) => checked[i.id]).map((i) => i.id);

  const doSend = async (body, msg) => {
    try {
      const r = await p(`/orders/${id}/send`, { by: user.name, ...body });
      toast.success(msg || t("c.sentOk", { n: r.sent, depts: r.departments.join(", ") }));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const giriInAttesa = [...new Set(pending.map((i) => i.giro || 1))].sort((a, b) => a - b);
  const primoGiro = giriInAttesa[0];

  // Righe identiche (stesso piatto, giro, nota e sovrapprezzo) si contano insieme: meno scroll,
  // e se le note sono diverse restano separate con la nota bene in vista.
  const gruppiPending = Object.values(
    pending.reduce((acc, i) => {
      const key = `${i.menu_item_id}|${i.giro || 1}|${i.note || ""}|${i.surcharge || 0}`;
      acc[key] = acc[key] || { key, righe: [], qty: 0 };
      acc[key].righe.push(i);
      acc[key].qty += i.qty;
      return acc;
    }, {})
  );

  return (
    <div className="fade-up space-y-5 pb-16 lg:pb-0" data-testid="comanda-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => nav(order.mode === "asporto" ? "/asporto" : "/sala")} data-testid="back-to-sala-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">
              {order.mode === "asporto" ? t("c.takeaway") : t("c.order")}
            </p>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              {order.mode === "asporto"
                ? `${order.table_numbers[0]}${order.customer_name ? ` · ${order.customer_name}` : ""}`
                : order.group_name || `${t("s.table")} ${order.table_numbers.join(" + ")}`}
            </h1>
            <p className="text-xs text-slate-500">
              {order.mode === "asporto"
                ? `${order.pickup_time ? `${t("c.pickup")} ${order.pickup_time} · ` : ""}${order.rider ? `rider ${order.rider} · ` : ""}${order.waiter}`
                : `${order.covers} ${t("s.covers")} · ${order.waiter}`}
            </p>
            {(order.guest_alerts?.length > 0 || order.occasion) && (
              <div className="mt-1 flex flex-wrap gap-1" data-testid="comanda-scheda-cliente">
                {order.occasion && (
                  <span className="rounded-full bg-fuchsia-100 px-2 py-0.5 text-[10px] font-bold text-fuchsia-900">
                    {order.occasion} oggi
                  </span>
                )}
                {(order.guest_allergens || []).map((a) => (
                  <span key={a} className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-900">
                    allergia: {a}
                  </span>
                ))}
                {(order.guest_alerts || [])
                  .filter((a) => !(order.guest_allergens || []).includes(a))
                  .map((a) => (
                    <span key={a} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                      {a}
                    </span>
                  ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Il totale deve poter essere «aperto»: si vede subito da cosa nasce */}
          <button onClick={() => (foglioPref ? setFoglioOpen(true) : setOrdinatoOpen(true))} data-testid="ordinato-btn"
                  className="flex h-12 flex-col justify-center rounded-md border border-slate-200 bg-white px-3 text-left active:bg-slate-100">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">già ordinato</span>
            <span className="font-num text-sm font-bold leading-tight">
              {sent.length} piatti{vedeImporti ? ` · ${eur(order.remaining)}` : ""}
            </span>
          </button>
          <Button variant="outline" size="icon" className="h-12 w-12" onClick={() => setFoglioOpen(true)} data-testid="foglio-btn" title="Vedi come sul foglio">
            <ClipboardList className="h-4 w-4" />
          </Button>
          {vedeImporti && (
            <Button className="h-12" onClick={() => setContoOpen(true)} data-testid="conto-btn">
              <Printer className="mr-1.5 h-4 w-4" /> {t("c.bill")}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="space-y-4 xl:col-span-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              className="h-11 pl-9"
              placeholder={t("c.search")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && shown.length) {
                  const primo = shown[0];
                  if (!(primo.sold_out || (primo.track_qty && primo.qty_left <= 0))) {
                    add(primo);
                    setQ("");
                  }
                }
              }}
              data-testid="menu-search-input"
            />
          </div>

          {!q && (
            <div className="space-y-2">
              {reparti.length > 1 && (
                <div className="flex flex-wrap gap-2" data-testid="reparti-comanda">
                  {reparti.map((r) => (
                    <button
                      key={r}
                      data-testid={`rep-${r.toLowerCase()}`}
                      onClick={() => { setRep(r); setCat(null); }}
                      className={`h-10 rounded-lg px-4 text-sm font-bold transition-colors ${
                        r === rep && !String(cat).startsWith("__") ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                  {vociFisse.length > 0 && (
                    <button
                      data-testid="rep-fisso"
                      onClick={() => setCat("__fisso")}
                      className={`h-10 rounded-lg px-4 text-sm font-bold transition-colors ${
                        cat === "__fisso" ? "bg-indigo-600 text-white" : "bg-white text-indigo-700 ring-1 ring-indigo-300"
                      }`}
                    >
                      Menù fisso
                    </button>
                  )}
                  {delGiorno.length > 0 && (
                    <button
                      data-testid="rep-giorno"
                      onClick={() => setCat("__giorno")}
                      className={`h-10 rounded-lg px-4 text-sm font-bold transition-colors ${
                        cat === "__giorno" ? "bg-emerald-600 text-white" : "bg-white text-emerald-700 ring-1 ring-emerald-300"
                      }`}
                    >
                      Piatti del giorno
                    </button>
                  )}
                  {consigliati.length > 0 && (
                    <button
                      data-testid="rep-consigliati"
                      onClick={() => setCat("__top")}
                      className={`h-10 rounded-lg px-4 text-sm font-bold transition-colors ${
                        cat === "__top" ? "bg-amber-500 text-white" : "bg-white text-amber-700 ring-1 ring-amber-200"
                      }`}
                    >
                      Più ordinati
                    </button>
                  )}
                </div>
              )}
              {!String(cat).startsWith("__") && (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="categorie-comanda">
                  {cats.map((c) => (
                    <button
                      key={c}
                      data-testid={`cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => setCat(c)}
                      className={`h-11 rounded-lg px-3 text-left text-xs font-semibold leading-tight transition-colors sm:text-sm ${
                        c === cat ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {cat === "__fisso" && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3" data-testid="fisso-sottomenu">
              <p className="text-xs font-bold uppercase tracking-wide text-indigo-900">Piatti del menù fisso di oggi</p>
              {fisso?.primi?.length || fisso?.secondi?.length || fisso?.contorni?.length ? (
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  {[["primi", "Primi"], ["secondi", "Secondi"], ["contorni", "Contorni"]].map(([k, label]) => (
                    <div key={k}>
                      <p className="text-[11px] font-bold uppercase text-indigo-700">{label}</p>
                      <ul className="mt-0.5 space-y-0.5">
                        {(fisso?.[k] || []).map((n) => (
                          <li key={n} className="text-[13px] leading-tight text-slate-700">· {n}</li>
                        ))}
                        {!(fisso?.[k] || []).length && <li className="text-[13px] text-slate-400">—</li>}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-sm text-slate-500">
                  Il menù di oggi non è ancora compilato: lo imposta il titolare nella voce «Menu fisso».
                </p>
              )}
              <p className="mt-2 text-[11px] text-indigo-800">
                Tocca qui sotto il menù da ordinare: scegli quante persone e a quale giro mandare ogni portata.
              </p>
            </div>
          )}

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            {shown.map((m) => {
              const out = m.sold_out || (m.track_qty && m.qty_left <= 0);
              const opzioni = m.by_weight || m.fixed_menu || m.needs_cooking || (m.extras || []).length > 0 || (m.variants || []).length > 0 || !!m.description || (m.tags || []).includes("componi") || (m.tags || []).includes("prezzo-aperto");
              const inCarrello = cart.filter((x) => x.menu_item_id === m.id).reduce((s, x) => s + x.qty, 0);
              return (
                <div key={m.id} className={`flex items-stretch border-b border-slate-100 last:border-0 ${out ? "opacity-50" : ""}`} data-testid={`menu-item-${m.id}`}>
                  {/* Si tocca la riga per aggiungere: in servizio è un gesto, non due */}
                  <button
                    className="min-w-0 flex-1 px-3 py-2.5 text-left active:bg-emerald-50"
                    disabled={out || m.fixed_menu || m.by_weight}
                    data-testid={`add-${m.id}`}
                    onClick={() => ((m.variants || []).length || (m.tags || []).includes("prezzo-aperto")
                      ? setOptionsFor(m)      // prima la scelta: naturale o frizzante, prezzo di oggi
                      : add(m))}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="min-w-0 flex-1 truncate text-[15px] font-semibold leading-tight">{m.name}</span>
                      {inCarrello > 0 && (
                        <span className="font-num shrink-0 rounded-full bg-slate-900 px-1.5 text-[11px] font-bold text-white" data-testid={`in-cart-${m.id}`}>
                          {inCarrello}
                        </span>
                      )}
                      <span className="font-num shrink-0 text-sm font-bold">
                        {m.by_weight && m.price_kg ? (
                          <span className="flex items-center gap-0.5 text-xs">
                            <Scale className="h-3 w-3" /> {eur(m.price_kg)}/kg
                          </span>
                        ) : (
                          eur(m.price)
                        )}
                      </span>
                    </div>
                    {m.description && (
                      <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-slate-500" data-testid={`ingredienti-${m.id}`}>
                        {m.description}
                      </p>
                    )}
                    {(m.allergens.length > 0 || m.tags.length > 0 || m.track_qty) && (
                      <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] leading-tight">
                        {m.allergens.length > 0 && (
                          <span className="font-medium text-rose-600" data-testid={`allergens-${m.id}`}>{m.allergens.join(", ")}</span>
                        )}
                        {m.tags.filter((tg) => tg !== "giorno").map((tg) => (
                          <span key={tg} className="inline-flex items-center gap-0.5 text-emerald-700">
                            <Leaf className="h-3 w-3" /> {tg}
                          </span>
                        ))}
                        {m.track_qty && (
                          <span className={out ? "text-slate-400" : m.qty_left < 3 ? "font-bold text-rose-600" : "font-bold text-emerald-600"}>
                            {out ? t("c.soldOut") : t("c.left", { n: m.qty_left })}
                          </span>
                        )}
                      </p>
                    )}
                  </button>

                  {m.surcharges.map((s) => (
                    <button
                      key={s.label}
                      disabled={out}
                      data-testid={`add-sur-${m.id}-${s.amount}`}
                      onClick={() => add(m, s.amount, s.label)}
                      className="shrink-0 border-l border-slate-100 px-2 text-[11px] font-semibold text-slate-600 active:bg-slate-100"
                    >
                      +{s.amount}€
                    </button>
                  ))}

                  {opzioni && (
                    <button
                      disabled={out}
                      data-testid={m.fixed_menu ? `componi-${m.id}` : `options-${m.id}`}
                      onClick={() => {
                        if (m.fixed_menu) {
                          setScelte({ primi: "", secondi: "", contorni: "" });
                          setComporre(m);
                        } else {
                          setOptionsFor(m);
                        }
                      }}
                      className="flex shrink-0 items-center gap-1 border-l border-slate-100 px-3 text-xs font-semibold text-slate-700 active:bg-slate-100"
                    >
                      {m.by_weight ? <Scale className="h-4 w-4" /> : m.fixed_menu ? <Plus className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          <Card className="p-5 shadow-lg" data-testid="cart-panel">
            <h3 className="text-lg font-semibold">{t("c.toAdd")}</h3>
            <div className="mt-3 space-y-3">
              {cart.length === 0 && <p className="text-sm text-slate-500">{t("c.empty")}</p>}
              {cart.map((i) => (
                <div key={i.key} className="flex items-center gap-2" data-testid={`cart-line-${i.menu_item_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{i.name}</p>
                    <p className="font-num text-[11px] text-slate-500">
                      {eur(i.price + i.surcharge + extraTotal(i))} {i.surcharge_label && `· ${i.surcharge_label}`}
                      {i.grams > 0 && ` · ${i.grams} g`}
                      {i.cooking && ` · ${i.cooking}`}
                      {i.takeaway && ` · ${t("x.opt.takeaway")}`}
                      {i.to_cut && ` · ${t("x.opt.cut")}`}
                    </p>
                    {(i.removed || []).length > 0 && (
                      <p className="truncate text-[11px] font-bold uppercase text-rose-600" data-testid={`cart-senza-${i.menu_item_id}`}>
                        senza {i.removed.join(", ")}
                      </p>
                    )}
                    {(i.extras || []).length > 0 && (
                      <p className="truncate text-[11px] text-amber-700" data-testid={`cart-scelte-${i.menu_item_id}`}>
                        {i.extras.map((e) => e.name).join(" · ")}
                      </p>
                    )}
                  </div>
                  <button onClick={() => chQty(i.key, -1)} className="rounded border p-1" data-testid={`cart-dec-${i.menu_item_id}`}>
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="font-num w-5 text-center text-sm font-bold">{i.qty}</span>
                  <button onClick={() => chQty(i.key, 1)} className="rounded border p-1" data-testid={`cart-inc-${i.menu_item_id}`}>
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                  <select
                    className="rounded border border-slate-200 px-1 py-1 text-[11px]"
                    value={i.giro}
                    data-testid={`cart-giro-${i.menu_item_id}`}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setCart((c) => c.map((x) => (x.key === i.key ? { ...x, giro: v } : x)));
                    }}
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>
                        {`${t("c.giroLabel")} ${n}`}{n === Math.max(i.course || 1, giroMandato + 1) ? " · consigliato" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            {cart.some((i) => i.giro !== Math.max(i.course || 1, giroMandato + 1)) && (
              <button
                data-testid="cart-giri-consigliati"
                className="mt-2 text-[11px] font-bold uppercase tracking-wider text-sky-800"
                onClick={() => setCart((c) => c.map((x) => ({ ...x, giro: Math.max(x.course || 1, giroMandato + 1) })))}
              >
                Rimetti i giri consigliati
              </button>
            )}
            <div className="mt-2 flex justify-end">
              <SpiegaGiri />
            </div>
            <Button className="mt-3 h-12 w-full" onClick={sendToOrder} data-testid="add-to-order-btn">
              {t("c.addToOrder")}
            </Button>
          </Card>

          {wines?.wines?.length > 0 && (
            <Card className="p-5" data-testid="wine-suggestions">
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <Wine className="h-4 w-4" /> {t("c.wines")}
              </h3>
              {wines.tips.map((t) => (
                <p key={t} className="mt-2 rounded-md bg-amber-50 p-2 text-xs text-amber-900">{t}</p>
              ))}
              {wines.reasons.map((r) => (
                <p key={r} className="mt-2 text-xs text-slate-500">{r}</p>
              ))}
              <div className="mt-3 flex flex-wrap gap-2">
                {wines.wines.map((w) => (
                  <Button key={w.id} size="sm" variant="outline" data-testid={`wine-${w.id}`} onClick={() => add(w)}>
                    {w.name} · {eur(w.price)}
                  </Button>
                ))}
              </div>
            </Card>
          )}

          {giri.sent.length > 0 && (
            <Card className="p-5" data-testid="giri-inviati-panel">
              <h3 className="text-lg font-semibold">In preparazione</h3>
              <div className="mt-3 space-y-3">
                {giri.sent.map((s) => (
                  <div key={s.giro} data-testid={`giro-stato-${s.giro}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-sm font-semibold">
                        {t("c.giroLabel")} {s.giro}
                        <span className="ml-1.5 text-[11px] font-medium text-slate-500">{s.departments.join(" · ")}</span>
                      </p>
                      <p className="font-num text-xs font-bold">
                        {s.all_ready ? "tutto pronto" : s.eta_min > 0 ? `in tavola fra ${s.eta_min}′` : "in ritardo"}
                      </p>
                    </div>
                    <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-200">
                      <div className={`h-full rounded-full transition-[width] duration-500 ${
                        s.all_ready ? "bg-emerald-500" : s.urgency === "ok" ? "bg-emerald-400"
                          : s.urgency === "warn" ? "bg-amber-400" : "bg-rose-500"}`}
                           style={{ width: `${Math.min(100, Math.round((s.minutes / Math.max(1, s.prep)) * 100))}%` }} />
                    </div>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      pronti {s.ready} di {s.total} · in cucina da {s.minutes}′
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="p-5" data-testid="pending-panel">
            <h3 className="text-lg font-semibold">
              {t("c.pending")}
              <span className="ml-2 font-num rounded-md bg-slate-900 px-2 py-0.5 text-xs font-bold text-white" data-testid="pending-table">
                {(order.table_numbers || []).join(" + ") || t("x.takeaway.title")}
              </span>
            </h3>
            {pending.length === 0 && <p className="mt-2 text-sm text-slate-500">{t("c.allSent")}</p>}
            <div className="mt-3 space-y-2">
              {gruppiPending.map((gr) => {
                const i = gr.righe[0];
                const tutteSel = gr.righe.every((r) => checked[r.id]);
                return (
                  <div key={gr.key} className="rounded-md border border-slate-200 p-2.5" data-testid={`pending-item-${i.id}`}>
                    <div className="flex items-start gap-2">
                      <Checkbox
                        checked={tutteSel}
                        onCheckedChange={(v) => {
                          const next = { ...checked };
                          gr.righe.forEach((r) => (next[r.id] = !!v));
                          setChecked(next);
                        }}
                        data-testid={`check-${i.id}`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold leading-tight">
                          <span className="font-num">{gr.qty}×</span> {i.name}
                        </p>
                        <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                          <span className={`rounded px-1.5 py-0.5 font-bold uppercase tracking-wide ${DEPT_BADGE[i.department] || "bg-slate-100 text-slate-700"}`} data-testid={`dept-badge-${i.id}`}>
                            {i.department}
                          </span>
                          <button data-testid={`giro-badge-${i.id}`} onClick={() => setGiroAperto(giroAperto === gr.key ? "" : gr.key)}
                                  className="rounded bg-slate-900 px-1.5 py-0.5 font-bold text-white">
                            {t("c.giroLabel")} {i.giro || 1} ✎
                          </button>
                          {i.surcharge_label && <span className="text-slate-500">{i.surcharge_label}</span>}
                          {(i.removed || []).length > 0 && (
                            <span className="font-bold uppercase text-rose-600" data-testid={`senza-${i.id}`}>
                              senza {i.removed.join(", ")}
                            </span>
                          )}
                          {i.note && <span className="font-medium text-amber-700">{i.note}</span>}
                          {gr.righe.length > 1 && <span className="text-slate-400">({gr.righe.length} righe uguali)</span>}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <button className="rounded border px-2 text-[11px]" data-testid={`note-${i.id}`}
                                onClick={() => { setNoteFor(i); setNoteText(i.note || ""); }}>
                          {t("c.note")}
                        </button>
                        <button className="rounded border p-1 text-rose-600" data-testid={`del-item-${i.id}`}
                                onClick={async () => { await del(`/orders/${id}/items/${i.righe ? i.id : i.id}`); load(); }}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    {giroAperto === gr.key && (
                      <div className="mt-2 flex flex-wrap items-center gap-1" data-testid={`giro-picker-${i.id}`}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button key={n} data-testid={`giro-${i.id}-${n}`}
                                  onClick={async () => {
                                    for (const r of gr.righe) await pa(`/orders/${id}/items/${r.id}`, { giro: n });
                                    setGiroAperto("");
                                    load();
                                  }}
                                  className={`rounded px-2.5 py-1.5 text-[11px] font-bold ${
                                    (i.giro || 1) === n ? "bg-slate-900 text-white"
                                      : n === (i.course || 1) ? "bg-sky-100 text-sky-800 ring-1 ring-sky-300"
                                        : "bg-slate-100 text-slate-600"}`}>
                            {n}{n === (i.course || 1) && (i.giro || 1) !== n ? "·" : ""}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {pending.length > 0 && (
              <div className="mt-4 space-y-2">
                <Button
                  className="h-12 w-full"
                  disabled={selIds.length === 0}
                  data-testid="send-checked-btn"
                  onClick={() => setSummary({ item_ids: selIds })}
                >
                  <Send className="mr-1.5 h-4 w-4" /> {t("c.send", { n: selIds.length })}
                </Button>
                <p className="text-center text-[11px] text-slate-500">
                  Spunta o togli le righe: parte solo ciò che è spuntato
                </p>
                {giriInAttesa.length > 1 && (
                  <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 pt-1">
                    {giriInAttesa.map((n) => {
                      const w = giri.waiting.find((x) => x.giro === n);
                      return (
                        <button key={n} data-testid={`send-giro-${n}`} onClick={() => doSend({ giro: n })}
                                className={`text-xs font-semibold underline decoration-slate-300 underline-offset-2 ${
                                  w?.suggested ? "text-emerald-700" : "text-slate-600"}`}>
                          oppure solo il giro {n}
                          {w?.suggested ? (
                            <span className="ml-1 rounded-full bg-emerald-600 px-1.5 py-0.5 text-[10px] font-bold text-white no-underline" data-testid={`giro-consigliato-${n}`}>
                              consiglio ora{w.after_min > 0 ? ` · giro in corso fra ${w.after_min}′` : ""}
                            </span>
                          ) : w?.send_in > 0 ? (
                            <span className="ml-1 text-[10px] font-bold text-slate-400 no-underline">
                              fra {w.send_in}′
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </Card>

          {sent.length > 0 && (
            <Card className="p-5" data-testid="sent-panel" id="sent-panel">
              <h3 className="text-lg font-semibold">Piatti mandati</h3>
              <div className="mt-3 space-y-1 text-sm">
                {sent.map((i) => (
                  <div key={i.id} className="flex flex-wrap items-center justify-between gap-2" data-testid={`sent-item-${i.id}`}>
                    <span className="min-w-0 truncate">
                      <span className="font-num">{i.qty}×</span> {i.name}
                      <span className="ml-1 text-[11px] text-slate-400">{i.department}</span>
                      {i.req && (
                        <span className="ml-1 block truncate text-[10px] font-semibold text-slate-500" data-testid={`req-state-${i.id}`}>
                          · {i.req.action === "prima" ? t("q.badgeFirst") : i.req.action === "attesa" ? t("q.badgeWait")
                            : i.req.action === "dopo" ? t("q.badgeAfter", { n: i.req.after_name }) : t("q.badgeVoid")}
                          {" — "}
                          {i.req.reply
                            ? (i.req.reply === "ok" ? t("q.replyOk") : i.req.reply === "in_cottura" ? t("q.replyCooking") : t("q.replyVoided"))
                            : i.req.seen ? t("q.seen") : t("q.sentReq")}
                        </span>
                      )}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${(ITEM_STATES[i.state] || ITEM_STATES.in_preparazione).cls}`}
                        data-testid={`state-${i.id}`}
                      >
                        {t(`st.${i.state || "in_preparazione"}`)}
                      </span>
                      {i.state !== "servito" && (
                        i.req?.reply ? (
                          <button
                            className="rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
                            data-testid={`req-read-${i.id}`}
                            onClick={async () => {
                              await p(`/orders/${id}/items/${i.id}/request-clear`);
                              load();
                            }}
                          >
                            {t("q.read")}
                          </button>
                        ) : i.req ? (
                          <button
                            className="rounded border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-500"
                            data-testid={`req-cancel-${i.id}`}
                            onClick={async () => {
                              try {
                                await p(`/orders/${id}/items/${i.id}/request-cancel`);
                                toast.success(t("q.cancelledOk"));
                                load();
                              } catch (e) {
                                toast.error(errMsg(e));
                                load();
                              }
                            }}
                          >
                            {t("q.cancelReq")}
                          </button>
                        ) : (
                          <button
                            className="rounded border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600"
                            data-testid={`req-open-${i.id}`}
                            onClick={() => setReqFor(i)}
                          >
                            <MessageSquare className="mr-0.5 inline h-3 w-3" /> {t("q.title")}
                          </button>
                        )
                      )}
                      {i.state !== "servito" && (
                        <button
                          className="rounded border border-slate-200 px-2 py-0.5 text-[11px] font-semibold"
                          data-testid={`served-${i.id}`}
                          onClick={async () => {
                            await p(`/orders/${id}/items/${i.id}/served`);
                            toast.success(t("c.servedOk"));
                            load();
                          }}
                        >
                          {t("c.served")}
                        </button>
                      )}
                      <button
                        className="rounded border border-rose-200 px-2 py-0.5 text-[11px] font-semibold text-rose-600"
                        data-testid={`cancel-sent-${i.id}`}
                        onClick={() => setCancelFor(i)}
                      >
                        <Ban className="mr-0.5 inline h-3 w-3" /> {t("x.cancel.btn")}
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={!!reqFor} onOpenChange={(v) => !v && (setReqFor(null), setAfterFor(null))}>
        <DialogContent data-testid="request-dialog">
          <DialogHeader>
            <DialogTitle>{reqFor ? `${reqFor.qty}× ${reqFor.name}` : t("q.title")}</DialogTitle>
          </DialogHeader>
          {afterFor ? (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-slate-500">{t("q.afterPick")}</p>
              {sent.filter((x) => x.id !== reqFor?.id).map((x) => (
                <Button
                  key={x.id}
                  variant="outline"
                  className="h-11 w-full justify-start"
                  data-testid={`req-after-${x.id}`}
                  onClick={async () => {
                    try {
                      await p(`/orders/${id}/items/${reqFor.id}/request`, { action: "dopo", after_id: x.id });
                      toast.success(t("q.reqOk"));
                      setReqFor(null);
                      setAfterFor(null);
                      load();
                    } catch (e) {
                      toast.error(errMsg(e));
                    }
                  }}
                >
                  <span className="min-w-0 truncate">
                    <span className="font-num">{x.qty}×</span> {x.name}
                  </span>
                  <span className="ml-auto shrink-0 text-[11px] font-normal text-slate-400">{x.department}</span>
                </Button>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {[
                { a: "attesa", label: t("q.wait"), Icon: Pause },
                { a: "prima", label: t("q.first"), Icon: Zap },
                { a: "dopo", label: t("q.after"), Icon: CornerDownRight },
                { a: "annulla", label: t("q.void"), Icon: Ban },
              ].map(({ a, label, Icon }) => (
                <Button
                  key={a}
                  variant="outline"
                  className="h-12 w-full justify-start text-left"
                  data-testid={`req-action-${a}`}
                  onClick={async () => {
                    if (a === "dopo") return setAfterFor(true);
                    try {
                      await p(`/orders/${id}/items/${reqFor.id}/request`, { action: a });
                      toast.success(t("q.reqOk"));
                      setReqFor(null);
                      load();
                    } catch (e) {
                      toast.error(errMsg(e));
                    }
                  }}
                >
                  <Icon className="mr-2 h-4 w-4 shrink-0" /> {label}
                </Button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!noteFor} onOpenChange={(v) => !v && setNoteFor(null)}>
        <DialogContent data-testid="note-dialog">
          <DialogHeader>
            <DialogTitle>{t("c.noteFor", { name: noteFor?.name })}</DialogTitle>
          </DialogHeader>
          <Input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="senza cipolla, cottura media…" data-testid="note-input" />
          <div className="flex flex-wrap gap-2" data-testid="note-chips">
            {(modifiche.length ? modifiche : ["senza cipolla", "cottura media", "ben cotta", "senza glutine"]).map((s) => (
              <Button
                key={s}
                size="sm"
                variant="outline"
                data-testid={`note-chip-${s.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setNoteText((v) => (v && v.toLowerCase().includes(s.toLowerCase()) ? v : [v, s].filter(Boolean).join(", ")))}
              >
                {s}
              </Button>
            ))}
            {noteText && (
              <Button size="sm" variant="ghost" data-testid="note-clear-btn" onClick={() => setNoteText("")}>
                pulisci
              </Button>
            )}
          </div>
          <Button
            className="h-11 w-full"
            data-testid="save-note-btn"
            onClick={async () => {
              await pa(`/orders/${id}/items/${noteFor.id}`, { note: noteText });
              setNoteFor(null);
              load();
            }}
          >
            {t("c.saveNote")}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!summary} onOpenChange={(v) => !v && setSummary(null)}>
        <DialogContent data-testid="send-summary-dialog">
          <DialogHeader>
            <DialogTitle>{t("c.summary")}</DialogTitle>
          </DialogHeader>
          <div className={foglioPref ? "rounded-sm bg-[#fdfbf4] p-4 font-mono text-[13px] text-neutral-900" : "space-y-3"} data-testid="summary-body">
            {departments.map((d) => {
              const rows = pending.filter((i) => (summary?.item_ids || []).includes(i.id) && i.department === d);
              if (!rows.length) return null;
              if (foglioPref)
                return (
                  <div key={d} className="mt-2 first:mt-0" data-testid={`summary-${d}`}>
                    <p className="border-b border-dashed border-neutral-400 pb-1 font-bold uppercase tracking-wide">{d}</p>
                    {rows.map((i) => (
                      <div key={i.id} className="mt-1.5 leading-tight">
                        <p className="flex gap-2">
                          <span className="w-8 shrink-0 font-bold">{i.qty}×</span>
                          <span className="flex-1">{i.name}</span>
                          <span className="shrink-0 text-neutral-500">giro {i.giro || 1}</span>
                        </p>
                        {(i.removed || []).length > 0 && (
                          <p className="ml-10 text-[12px] font-bold uppercase text-neutral-900">
                            senza {i.removed.join(", ")}
                          </p>
                        )}
                        {(i.note || i.surcharge_label || i.allergens?.length > 0) && (
                          <p className="ml-10 text-[12px] text-neutral-600">
                            {[i.surcharge_label, i.note, i.allergens?.length ? `allergeni: ${i.allergens.join(", ")}` : ""]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                );
              return (
                <div key={d} className="rounded-md border border-slate-200 p-3" data-testid={`summary-${d}`}>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{d}</p>
                  {rows.map((i) => (
                    <p key={i.id} className="mt-1 text-sm">
                      <span className="font-num font-semibold">{i.qty}×</span> {i.name}
                      {(i.removed || []).length > 0 && (
                        <span className="ml-1 text-[11px] font-bold uppercase text-rose-600">senza {i.removed.join(", ")}</span>
                      )}
                      <span className="text-xs text-slate-500">
                        {" "}· {t("c.giroLabel")} {i.giro || 1}
                        {i.note && ` · ${i.note}`}
                        {i.surcharge_label && ` · ${i.surcharge_label}`}
                      </span>
                      {i.allergens?.length > 0 && (
                        <span className="ml-1 text-[11px] font-medium text-rose-600">{i.allergens.join(", ")}</span>
                      )}
                    </p>
                  ))}
                </div>
              );
            })}
          </div>
          <Button
            className="h-12 w-full"
            data-testid="confirm-send-btn"
            onClick={async () => {
              const body = summary;
              setSummary(null);
              await doSend(body);
            }}
          >
            {t("c.confirmSend")}
          </Button>
        </DialogContent>
      </Dialog>

      <FoglioComanda
        order={order}
        open={foglioOpen}
        onClose={() => setFoglioOpen(false)}
        eur={eur}
        vedeImporti={vedeImporti}
        locale={settings?.business_name}
        stampa={settings?.printing_on !== false}
        preferita={foglioPref}
        setPreferita={(v) => {
          setFoglioPref(v);
          localStorage.setItem("rh_vista_comanda", v ? "foglio" : "lista");
        }}
        onLista={() => {
          setFoglioOpen(false);
          setOrdinatoOpen(true);
        }}
      />

      <AggiungiPiatto item={optionsFor} ingredienti={ingredienti} onClose={() => setOptionsFor(null)} onAdd={(opts) => add(optionsFor, opts.surcharge || 0, opts.surchargeLabel || "", opts)} onSaved={() => g("/menu").then(setMenu)} />
      {/* Menu fisso: il cameriere scegle il primo, il secondo e il contorno di quel cliente */}
      <Dialog open={!!comporre} onOpenChange={(v) => !v && setComporre(null)}>
        <DialogContent data-testid="menu-fisso-dialog">
          <DialogHeader>
            <DialogTitle>{comporre?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2">
              <span className="text-sm font-semibold text-slate-700">Quanti menu</span>
              <div className="ml-auto flex items-center gap-2">
                <Button size="sm" variant="outline" data-testid="fisso-qty-meno"
                        onClick={() => setFissoQty((n) => Math.max(1, n - 1))}>−</Button>
                <span className="font-num w-8 text-center text-lg font-bold" data-testid="fisso-qty">{fissoQty}</span>
                <Button size="sm" variant="outline" data-testid="fisso-qty-piu"
                        onClick={() => setFissoQty((n) => Math.min(20, n + 1))}>+</Button>
              </div>
            </div>
            {[["primi", "Primo", 1], ["secondi", "Secondo", 2], ["contorni", "Contorno", 2]].map(([k, label]) => (
              <div key={k} className="space-y-1 rounded-lg border border-slate-200 p-2">
                <label className="block space-y-1 text-sm">
                  <span className="font-semibold text-slate-700">{label}</span>
                  <select
                    className="h-12 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                    value={scelte[k]}
                    onChange={(e) => {
                      const nome = e.target.value;
                      setScelte({ ...scelte, [k]: nome });
                      // Giro consigliato: quello della portata stessa (primo col primo, secondo col secondo)
                      const piatto = menu.find((x) => x.name === nome);
                      if (piatto?.course) setFissoGiri((gr) => ({ ...gr, [k]: piatto.course }));
                    }}
                    data-testid={`scelta-${k}`}
                  >
                    <option value="">— senza {label.toLowerCase()} —</option>
                    {(fisso?.[k] || []).map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </label>
                {scelte[k] && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-slate-500">manda col giro</span>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        data-testid={`fisso-giro-${k}-${n}`}
                        onClick={() => setFissoGiri({ ...fissoGiri, [k]: n })}
                        className={`font-num h-11 w-11 rounded-md text-sm font-bold ${
                          fissoGiri[k] === n ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {!fisso?.primi?.length && (
              <p className="text-sm text-slate-500">
                Il menu del giorno non è ancora compilato: lo imposta il titolare nella voce «Menu fisso».
              </p>
            )}
            <p className="text-xs text-slate-500">
              Ogni portata diventa una riga sua: in cucina la vedono separata per giro e tu mandi un giro
              per volta, così non esce tutto insieme.
            </p>
            <Button
              className="h-12 w-full"
              disabled={!Object.values(scelte).some(Boolean)}
              data-testid="conferma-menu-fisso"
              onClick={() => {
                // Riga del prezzo: il menu fisso vale una volta, nel giro della prima portata.
                const primoGiro = Math.min(...["primi", "secondi", "contorni"].filter((k) => scelte[k]).map((k) => fissoGiri[k]));
                add(comporre, 0, "", { qty: fissoQty, giro: primoGiro, solo_conto: true });
                // Una riga per portata, a prezzo zero: è già compresa nel menu.
                ["primi", "secondi", "contorni"].forEach((k) => {
                  const nome = scelte[k];
                  if (!nome) return;
                  const piatto = menu.find((x) => x.name === nome);
                  add(piatto || comporre, 0, "", {
                    qty: fissoQty, giro: fissoGiri[k], incluso: true, nome,
                  });
                });
                setComporre(null);
                setFissoQty(1);
                setFissoGiri({ primi: 1, secondi: 2, contorni: 2 });
              }}
            >
              Aggiungi {fissoQty > 1 ? `${fissoQty} menu` : "al tavolo"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <AnnullaPiatto item={cancelFor} reasons={reasons} onClose={() => setCancelFor(null)} onConfirm={cancelItem} />

      {/* Telefono: il carrello resta sotto le dita. Va fuori dal contenitore animato,
          altrimenti il `transform` dell'animazione annulla la posizione fissa. */}
      {cart.length > 0 && createPortal(
        <div
          className="fixed left-0 right-0 z-40 flex items-center gap-3 border-t border-slate-200 bg-white px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.12)] lg:hidden"
          style={{ bottom: "calc(56px + env(safe-area-inset-bottom))" }}
          data-testid="cart-bar-mobile"
        >
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-slate-500">{t("c.toAdd")}</p>
            <p className="font-num text-sm font-bold" data-testid="cart-bar-total">
              {cart.reduce((s, i) => s + i.qty, 0)} × · {eur(cart.reduce((s, i) => s + (i.price + i.surcharge + extraTotal(i)) * i.qty, 0))}
            </p>
          </div>
          <Button className="h-12 shrink-0 whitespace-nowrap px-5" onClick={sendToOrder} data-testid="cart-bar-send-btn">
            {t("c.addShort")}
          </Button>
        </div>,
        document.body
      )}

      <Dialog open={ordinatoOpen} onOpenChange={setOrdinatoOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="ordinato-dialog">
          <DialogHeader>
            <DialogTitle>Già ordinato{vedeImporti ? ` · ${eur(order.remaining)}` : ""}</DialogTitle>
          </DialogHeader>
          {sent.length === 0 ? (
            <p className="text-sm text-slate-500">Non è ancora stato mandato niente in cucina.</p>
          ) : (
            <div className="space-y-3">
              {[...new Set(sent.map((i) => i.giro || 1))].sort((a, b) => a - b).map((n) => (
                <div key={n} data-testid={`ordinato-giro-${n}`}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                    {t("c.giroLabel")} {n}
                  </p>
                  {sent.filter((i) => (i.giro || 1) === n).map((i) => (
                    <div key={i.id} className="flex items-baseline gap-2 border-b border-slate-100 py-1.5 last:border-0" data-testid={`ordinato-item-${i.id}`}>
                      <span className="font-num text-sm font-bold">{i.qty}×</span>
                      <span className="min-w-0 flex-1">
                        <span className="text-sm">{i.name}</span>
                        {i.note && <span className="block text-[11px] text-amber-700">{i.note}</span>}
                        <span className={`ml-0 mt-0.5 inline-block rounded-full px-1.5 text-[10px] font-bold uppercase ${(ITEM_STATES[i.state] || ITEM_STATES.in_preparazione).cls}`}>
                          {t(`st.${i.state || "in_preparazione"}`)}
                        </span>
                      </span>
                      {vedeImporti && (
                        <span className="font-num shrink-0 text-sm font-semibold">{eur((i.price + (i.surcharge || 0)) * i.qty)}</span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-slate-200 pt-2">
                <span className="text-sm font-semibold">{vedeImporti ? "Totale da pagare" : "Piatti mandati"}</span>
                <span className="font-num text-lg font-bold">
                  {vedeImporti ? eur(order.remaining) : sent.reduce((s, i) => s + i.qty, 0)}
                </span>
              </div>
              {vedeImporti && (
                <Button variant="outline" className="w-full" data-testid="ordinato-conto-btn"
                        onClick={() => { setOrdinatoOpen(false); setContoOpen(true); }}>
                  <Printer className="mr-1.5 h-4 w-4" /> Vai al conto
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Conto open={contoOpen} onClose={() => setContoOpen(false)} order={order} reload={load} onClosed={() => nav(order.mode === "asporto" ? "/asporto" : "/sala")} />
    </div>
  );
}
